import { Outlet, useLocation } from "react-router-dom";
import { Alert, Skeleton } from "antd";
import { useEmbedToken } from "../../context/EmbedTokenContext";

// Embed-mode tenant-admin gate (PARTNER_PORTAL_TOGGLES + Module 11).
//
// Companion to <TenantAdminRoute />, which reads `useAuth().user.role`
// from AuthContext. AuthContext hydrates from `localStorage.user` —
// embed mode keeps the SSO JWT in memory only and never writes the
// user shape into localStorage, so AuthContext is `null` inside the
// iframe even for a tenant_admin SSO token. Reusing TenantAdminRoute
// here would 401-redirect every embed admin.
//
// This guard reads the role straight from the SSO JWT claims via
// `useEmbedToken()`. Same allowed-role set as the non-embed gate
// (`tenant_admin` + `super_admin`). Forbidden-role rendering uses an
// embed-styled <Alert /> rather than the non-embed <Result /> 403
// page, because the iframe has no global header/footer chrome and a
// big "Back to dashboard" button isn't meaningful here.

const TENANT_ADMIN_ROLES = new Set(["tenant_admin", "super_admin"]);

const TenantAdminEmbedRoute = () => {
  const { ready, claims, token } = useEmbedToken();
  const location = useLocation();

  // Token bootstrap is in flight — match EmbedShellInner's loading
  // skeleton so the iframe doesn't flash an empty frame.
  if (!ready) {
    return (
      <div className="p-8">
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  // No token (SSO didn't deliver) — let EmbedShellInner's outer guard
  // surface the canonical "session expired" alert. Returning a hard
  // <Navigate to="/login" /> would punt the iframe out to the tenant
  // login page, which is the wrong context inside a partner host.
  if (!token) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <Alert
          type="error"
          showIcon
          message="Session unavailable"
          description="No active session. Reopen this view from your host application."
        />
      </div>
    );
  }

  if (!TENANT_ADMIN_ROLES.has(claims?.role)) {
    // Regular SSO users hitting /embed/team directly land here.
    // Match the styling of EmbedShellInner's "Not available" surface
    // for consistency (same Alert type=error, same max-width).
    return (
      <div className="p-8 max-w-xl mx-auto" data-pathname={location.pathname}>
        <Alert
          type="error"
          showIcon
          message="Not available"
          description="Team management is only available to organization admins."
        />
      </div>
    );
  }

  return <Outlet />;
};

export default TenantAdminEmbedRoute;
